import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLiveStatus } from '../contexts/LiveStatusContext';
import { useConfig } from '../config/ConfigContext';
import { supabase } from '../lib/supabase';
import { getMobileType } from '../utils/deviceDetection';
import '../styles/liveBanner.css';

const LiveBanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { config } = useConfig();
  const { hasLiveVideos } = useLiveStatus();
  const [show, setShow] = useState(false);
  const [liveVideoInfo, setLiveVideoInfo] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  // Check device type
  const deviceType = getMobileType();
  const isDesktop = deviceType === 'desktop';

  // Don't show on video watch pages
  const isVideoWatchPage = location.pathname.startsWith('/videos/') && location.pathname !== '/videos';

  useEffect(() => {
    const fetchLiveVideoInfo = async () => {
      if (!hasLiveVideos || dismissed || isVideoWatchPage || !config?.center_id) {
        setShow(false);
        return;
      }

      try {
        // Get the current live video info — filtered by center + staleness threshold
        // Must match LiveStatusContext threshold (4 hours) to stay in sync
        const LIVE_STALENESS_HOURS = 4;
        const staleThreshold = new Date(Date.now() - LIVE_STALENESS_HOURS * 60 * 60 * 1000).toISOString();
        const { data, error } = await supabase
          .from('videos')
          .select('youtube_video_id, title')
          .eq('center_id', config.center_id)
          .eq('is_live_now', true)
          .eq('privacy_status', 'public')
          .gte('published_at', staleThreshold)
          .limit(1)
          .single();

        if (!error && data) {
          setLiveVideoInfo(data);
          setShow(true);

          // Auto-dismiss after 10 seconds
          const timer = setTimeout(() => {
            setShow(false);
          }, 10000);

          return () => clearTimeout(timer);
        }
      } catch (error) {
        console.error('Error fetching live video info:', error);
      }
    };

    fetchLiveVideoInfo();
  }, [hasLiveVideos, dismissed, isVideoWatchPage, config?.center_id]);

  // Reset dismissed state when live status changes
  useEffect(() => {
    if (!hasLiveVideos) {
      setDismissed(false);
    }
  }, [hasLiveVideos]);

  const handleClick = () => {
    if (liveVideoInfo) {
      // Navigate to the live video
      const videoId = liveVideoInfo.youtube_video_id;
      navigate(`/videos/${videoId}`, { state: { autoplay: true } });
      setShow(false);
    }
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    setShow(false);
    setDismissed(true);
  };

  if (!show || !liveVideoInfo || isDesktop) return null;

  return (
    <div className={`live-banner ${show ? 'show' : ''}`} onClick={handleClick}>
      <div className="live-banner-content">
        <div className="live-banner-left">
          <span className="live-badge">
            <span className="live-dot"></span>
            LIVE NOW
          </span>
          <span className="live-title">{liveVideoInfo.title}</span>
        </div>
        <button className="live-banner-close" onClick={handleDismiss} aria-label="Dismiss">
          ×
        </button>
      </div>
    </div>
  );
};

export default LiveBanner;